import React from 'react'
import styles from './ChatBubble.module.css'

export interface ChatBubbleProps {
  /**
   * Whether the chat is currently open
   */
  isOpen: boolean

  /**
   * Callback when bubble is clicked
   */
  onClick: () => void

  /**
   * Primary brand color for bubble background
   * @default '#0066CC'
   */
  primaryColor?: string

  /**
   * Number of unread messages to display in badge
   * @default 0
   */
  unreadCount?: number

  /**
   * Custom text for bubble tooltip
   * @default 'Chat with us'
   */
  tooltipText?: string
}

/**
 * Floating chat bubble button
 * Displays in bottom-right corner with badge for unread messages
 */
export const ChatBubble: React.FC<ChatBubbleProps> = ({
  isOpen,
  onClick,
  primaryColor = '#0066CC',
  unreadCount = 0,
  tooltipText = 'Chat with us',
}) => {
  const handleClick = () => {
    onClick()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <button
      type="button"
      className={`${styles.chatBubble} ${isOpen ? styles.open : styles.closed}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{ backgroundColor: primaryColor }}
      aria-label={tooltipText}
      aria-expanded={isOpen}
      data-testid="chat-bubble"
    >
      {/* Badge for unread count */}
      {unreadCount > 0 && !isOpen && (
        <span className={styles.badge} data-testid="unread-badge" aria-label={`${unreadCount} unread messages`}>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}

      {/* Icon */}
      <div className={styles.icon}>
        {isOpen ? (
          // Close icon (X)
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          // Chat icon
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {/* Tooltip */}
      {!isOpen && <span className={styles.tooltip}>{tooltipText}</span>}
    </button>
  )
}
